import {
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import { trackBulkDeleteEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import type { RestDay, Shift, ShiftsRow } from 'modules/shift_management/lib/types'

interface Props {
  shifts: Shift[]
  restDays: RestDay[]
  destroyBackupShifts: boolean
  destroyAutomaticOnly?: boolean
}

export const useDestroyShiftsAndRestDays = ({
  shifts,
  restDays,
  destroyBackupShifts,
  destroyAutomaticOnly = false,
}: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()
  const shiftIds = uniq(shifts.map((shift) => shift.id))
  const restDayIds = restDays.map((restDay) => restDay.id)

  const { employeeCount } = shiftManagementCounts(shifts, restDays)

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
  })

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackBulkDeleteEvent(
        shifts,
        restDays,
        view,
        employeeCount,
        shiftsConflicts,
        'top_bar_button',
        destroyAutomaticOnly
      )

      await destroyRestDays({
        variables: { ids: restDayIds },
        update: (cache, { data }) => {
          data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
            (restDay) => {
              cache.evict({
                id: cache.identify(restDay),
              })
            }
          )
        },
      })

      await destroyShifts({
        variables: {
          ids: shiftIds,
          destroyBackupShifts,
          destroyAutomaticShifts: destroyAutomaticOnly,
        },
        update: (cache, { data }) => {
          data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
            cache.evict({
              id: cache.identify(shift),
            })
          })
        },
      })

      // Get shiftsRows where there are affected shifts to update stats
      const shiftKeys = new Set(uniq(shifts.map((shift) => shift.rowKeyReference(view))))
      const restDayKeys = new Set(restDayIds)
      const previousShiftsRowsFromShifts = shiftsRows.filter((row: ShiftsRow) =>
        shiftKeys.has(row.key)
      )
      const previousShiftsRowsFromRestDays = shiftsRows.filter((row: ShiftsRow) =>
        restDayKeys.has(String(row.key))
      )

      const previousShiftsRows = uniq([
        ...previousShiftsRowsFromRestDays,
        ...previousShiftsRowsFromShifts,
      ])

      await refreshStats(previousShiftsRows)

      const shiftsToRefresh = shifts.filter((shift) => {
        const shouldIncludeByBackup = destroyBackupShifts || !shift.isBackup
        const shouldIncludeByAutomatic = !destroyAutomaticOnly || shift.isAutomatic

        return shouldIncludeByBackup && shouldIncludeByAutomatic
      })

      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: shiftsToRefresh, view })
      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays })
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      closePortal()
    },
    {
      successMessage: `${i18n.t('shift_management.sidepanel.rest_days')} ${i18n.t(
        'shift_management.add_shift.and'
      )} ${i18n.t('shift_management.actions.delete_shifts_success').toLowerCase()}`,
    }
  )

  return {
    performAction,
    submitting,
  }
}
